import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ChevronDown, Plus, Trash2, LogOut, Share2, Bell, Settings, ShoppingCart, User, DoorOpen, ArrowUpDown } from 'lucide-react';

const APP_ICON = "https://static.prod-images.emergentagent.com/jobs/3c404c7b-5045-4d45-8ddd-9111c2fbc8c7/images/1edd49db85dba9cc04f46bd52929381e881d2b7dac026dd5c68dd6bec34e756c.png";

export default function Header({
  lists, activeListId, onSelectList, onCreateList, onDeleteList, onLeaveList,
  invitationCount, onShowInvitations, onShowShare, onShowGroupSort, isOwner, user, onLogout
}) {
  const navigate = useNavigate();
  const [showNewList, setShowNewList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const activeList = lists.find(l => l.id === activeListId);

  const handleCreateList = () => {
    if (!newListName.trim()) return;
    onCreateList(newListName.trim());
    setNewListName('');
    setShowNewList(false);
  };

  return (
    <>
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-white/40 shadow-sm" data-testid="app-header">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-2">
          <img src={APP_ICON} className="w-8 h-8 rounded-lg shrink-0" alt="Logo" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="font-sans font-medium text-[#2F3E3B] hover:bg-[#EAE6DF]/50 gap-1 max-w-[200px] truncate"
                data-testid="list-selector-dropdown">
                <ShoppingCart className="w-4 h-4 shrink-0 text-[#7F9C82]" />
                <span className="truncate">{activeList?.name || 'Vælg liste'}</span>
                <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64 bg-white border-[#EAE6DF]">
              {lists.map(list => (
                <DropdownMenuItem key={list.id} onClick={() => onSelectList(list.id)}
                  className={`font-sans cursor-pointer flex items-center justify-between group ${list.id === activeListId ? 'bg-[#7F9C82]/10' : ''}`}
                  data-testid={`list-option-${list.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className={`truncate ${list.id === activeListId ? 'font-medium text-[#7F9C82]' : 'text-[#2F3E3B]'}`}>{list.name}</span>
                      {!list.is_owner && <span className="text-[10px] text-[#5C706A]">(delt - {list.owner_name || 'ukendt'})</span>}
                    </div>
                  </div>
                  {!list.is_default && list.is_owner && (
                    <button onClick={(e) => { e.stopPropagation(); onDeleteList(list.id); }}
                      className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-50 text-[#C85547] transition-opacity"
                      data-testid={`delete-list-${list.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                  {!list.is_owner && (
                    <button onClick={(e) => { e.stopPropagation(); onLeaveList(list.id); }}
                      className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-50 text-[#C85547] transition-opacity"
                      data-testid={`leave-list-${list.id}`}><DoorOpen className="w-3.5 h-3.5" /></button>
                  )}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowNewList(true)} className="font-sans text-[#7F9C82] cursor-pointer"
                data-testid="new-list-option">
                <Plus className="w-4 h-4 mr-2" />Ny liste
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex-1" />

          <Button variant="ghost" size="sm" onClick={onShowGroupSort}
            className="text-[#5C706A] hover:text-[#2F3E3B] hover:bg-[#EAE6DF]/50" title="Sortér varegrupper"
            data-testid="sort-groups-button">
            <ArrowUpDown className="w-4 h-4" />
          </Button>

          {isOwner && (
            <Button variant="ghost" size="sm" onClick={onShowShare} className="text-[#5C706A] hover:text-[#2F3E3B] hover:bg-[#EAE6DF]/50"
              data-testid="share-list-button">
              <Share2 className="w-4 h-4" />
            </Button>
          )}

          <Button variant="ghost" size="sm" onClick={onShowInvitations}
            className="text-[#5C706A] hover:text-[#2F3E3B] hover:bg-[#EAE6DF]/50 relative" data-testid="invitations-button">
            <Bell className="w-4 h-4" />
            {invitationCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#C87965] text-white text-[10px] rounded-full flex items-center justify-center font-sans">
                {invitationCount}
              </span>
            )}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-[#5C706A] hover:text-[#2F3E3B] hover:bg-[#EAE6DF]/50"
                data-testid="user-menu-button">
                <User className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-white border-[#EAE6DF]">
              <div className="px-3 py-2 border-b border-[#EAE6DF]">
                <p className="font-sans font-medium text-sm text-[#2F3E3B]">{user?.name}</p>
                <p className="font-sans text-xs text-[#5C706A]">{user?.email}</p>
              </div>
              {user?.role === 'admin' && (
                <DropdownMenuItem onClick={() => navigate('/admin')} className="font-sans cursor-pointer" data-testid="admin-link">
                  <Settings className="w-4 h-4 mr-2" />Administration
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onLogout} className="font-sans text-[#C85547] cursor-pointer" data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" />Log ud
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <Dialog open={showNewList} onOpenChange={setShowNewList}>
        <DialogContent className="bg-white border-[#EAE6DF]">
          <DialogHeader><DialogTitle className="text-[#2F3E3B]">Ny indkøbsliste</DialogTitle></DialogHeader>
          <div className="py-2">
            <Input value={newListName} onChange={e => setNewListName(e.target.value)} placeholder="Navn på listen"
              className="border-[#EAE6DF]" data-testid="new-list-name-input"
              onKeyDown={e => e.key === 'Enter' && handleCreateList()} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewList(false)} className="font-sans">Annuller</Button>
            <Button onClick={handleCreateList} disabled={!newListName.trim()}
              className="bg-[#7F9C82] hover:bg-[#658368] text-white font-sans" data-testid="create-list-button">Opret</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
